import { useState } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { Fade } from 'react-reveal';

const NAME_REGEX = /^[A-Za-z\s.'-]{2,50}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10}$/;

const validators = {
  name: (value) => {
    if (!value.trim()) return 'Full name is required.';
    if (!NAME_REGEX.test(value.trim())) return 'Enter a valid name (letters only, 2-50 characters).';
    return '';
  },
  email: (value) => {
    if (!value.trim()) return 'Email address is required.';
    if (!EMAIL_REGEX.test(value.trim())) return 'Enter a valid email address.';
    return '';
  },
  phone: (value) => {
    if (!value.trim()) return 'Phone number is required.';
    if (!PHONE_REGEX.test(value.trim())) return 'Enter a valid 10-digit phone number.';
    return '';
  },
  subject: (value) => {
    if (!value.trim()) return 'Subject is required.';
    if (value.trim().length < 3) return 'Subject must be at least 3 characters.';
    return '';
  },
  message: (value) => {
    if (!value.trim()) return 'Message is required.';
    if (value.trim().length < 10) return 'Message must be at least 10 characters.';
    if (value.trim().length > 1000) return 'Message must be under 1000 characters.';
    return '';
  }
};

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
};

const ContactUsFormCard = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [error, setError] = useState(null);

  const validateField = (id, value) => validators[id] ? validators[id](value) : '';

  const validateAll = (data) => {
    const newErrors = {};
    Object.keys(validators).forEach((key) => {
      const message = validateField(key, data[key]);
      if (message) newErrors[key] = message;
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { id } = e.target;
    let { value } = e.target;

    if (id === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    const updatedData = { ...formData, [id]: value };
    setFormData(updatedData);

    // Live validation as the user types, only after the field has been touched once
    if (touched[id]) {
      setErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
    }
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    setTouched((prev) => ({ ...prev, [id]: true }));
    setErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResponseMessage('');

    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      phone: true,
      subject: true,
      message: true
    });

    if (Object.keys(newErrors).length > 0) {
      setError('Please fix the highlighted fields before submitting.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('https://api.optionbricks.in/enquiry-now', formData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.status === 200) {
        setResponseMessage('Your enquiry has been submitted successfully!');
        setFormData(initialFormData);
        setErrors({});
        setTouched({});
      } else {
        throw new Error('Something went wrong');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = (id) =>
    `w-full bg-zinc-50 border ${
      errors[id] && touched[id] ? 'border-red-400' : 'border-zinc-200'
    } text-zinc-900 px-2 py-3.5 rounded-xl focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all duration-300 placeholder:text-zinc-400 font-medium shadow-none`;

  return (
    <Fade bottom>
      <div className="relative bg-white rounded-[2rem] w-full max-w-2xl mx-auto z-10 font-sans">

        {/* Sleek Form Container */}
        <div className="relative z-10 flex flex-col h-full">

          <Form onSubmit={handleSubmit} className="space-y-5" noValidate>

            <Form.Group controlId="name">
              <Form.Label className="block text-xs font-bold text-zinc-900 uppercase tracking-widest mb-2">
                Full Name <span className="text-red-500">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(errors.name && touched.name)}
                className={fieldClass('name')}
              />
              {errors.name && touched.name && (
                <p className="mt-1.5 text-xs font-medium text-red-500">{errors.name}</p>
              )}
            </Form.Group>

            <Form.Group controlId="email">
              <Form.Label className="block text-xs font-bold text-zinc-900 uppercase tracking-widest mb-2">
                Email Address <span className="text-red-500">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(errors.email && touched.email)}
                className={fieldClass('email')}
              />
              {errors.email && touched.email && (
                <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email}</p>
              )}
            </Form.Group>

            <Form.Group controlId="phone">
              <Form.Label className="block text-xs font-bold text-zinc-900 uppercase tracking-widest mb-2">
                Phone Number <span className="text-red-500">*</span>
              </Form.Label>
              <Form.Control
                type="tel"
                placeholder="9876543210"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(errors.phone && touched.phone)}
                className={fieldClass('phone')}
              />
              {errors.phone && touched.phone && (
                <p className="mt-1.5 text-xs font-medium text-red-500">{errors.phone}</p>
              )}
            </Form.Group>

            <Form.Group controlId="subject">
              <Form.Label className="block text-xs font-bold text-zinc-900 uppercase tracking-widest mb-2">
                Subject <span className="text-red-500">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="How can we help you?"
                value={formData.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(errors.subject && touched.subject)}
                className={fieldClass('subject')}
              />
              {errors.subject && touched.subject && (
                <p className="mt-1.5 text-xs font-medium text-red-500">{errors.subject}</p>
              )}
            </Form.Group>

            <Form.Group controlId="message">
              <Form.Label className="block text-xs font-bold text-zinc-900 uppercase tracking-widest mb-2">
                Message <span className="text-red-500">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Tell us about your inquiry..."
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(errors.message && touched.message)}
                className={`${fieldClass('message')} resize-none`}
              />
              <div className="flex justify-between mt-1.5">
                {errors.message && touched.message ? (
                  <p className="text-xs font-medium text-red-500">{errors.message}</p>
                ) : <span />}
                <span className="text-xs text-zinc-400">{formData.message.length}/1000</span>
              </div>
            </Form.Group>

            {/* Success Alert */}
            {responseMessage && (
              <Alert
                variant="success"
                className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 shadow-lg px-5 py-4"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                    <svg
                      className="h-6 w-6 text-emerald-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-emerald-800">
                      Success!
                    </h4>
                    <p className="mt-1 text-sm text-emerald-700 leading-relaxed">
                      {responseMessage}
                    </p>
                  </div>

                  {/* Close Button */}
                  <button
                    type="button"
                    onClick={() => setResponseMessage("")}
                    className="rounded-full p-1 text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700 transition"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Left Accent */}
                <div className="absolute left-0 top-0 h-full w-1.5 bg-emerald-500"></div>
              </Alert>
            )}

            {error && (
              <Alert variant="danger" className="bg-red-50 text-red-600 border border-red-200 rounded-xl p-4 text-sm font-medium flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </Alert>
            )}

            {/* Premium Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full mt-4 flex items-center justify-center gap-2 !bg-[#1e6091] hover:!bg-[#185076] !text-white !border-none font-bold text-base md:text-lg py-4 rounded-xl shadow-[0_10px_20px_rgba(30,96,145,0.2)] hover:shadow-[0_15px_30px_rgba(30,96,145,0.35)] hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="text-white" />
                  <span>Processing...</span>
                </>
              ) : (
                'Submit Request'
              )}
            </Button>

          </Form>
        </div>
      </div>
    </Fade>
  );
};

export default ContactUsFormCard;